<template>
  <div>
    <v-row>
      <v-col cols="12">
        <h3 class="mb-0 section-title">{{ title }}</h3>
      </v-col>

      <mf-toggle v-model="isBlackListBlocked.active" class="ml-4" hide-details label="Bloqueio de CPFs" @change="updateBlackListBlock" />

      <v-col cols="12">
        <v-row>
          <v-spacer />
          <mf-tooltip :disabled="hasPiiAccess" max-width="250" top content-class="truncated-tooltip">
            <template #activator="{ on }">
              <mf-button class="mr-3" label="Bloquear CPF" :disabled="!isBlackListBlocked.active || !hasPiiAccess" @click="goToBlockPage" />
            </template>
            <span>Você precisa ter a permissão de acesso a informações privilegiadas para acessar esse recurso.</span>
          </mf-tooltip>
        </v-row>
      </v-col>
    </v-row>
    <mf-card-container v-model="search" title="Lista de pessoas" use-search placeholder="Buscar por CPF">
      <template v-slot:filters>
        <export-data :disabled="!hasPiiAccess" class="mx-2" :data-fetcher="getDataToExport" filename="lista_de_pessoas" ghost />
      </template>
      <paginated-data-table
        ref="blockedUsers"
        :headers="headers"
        :search="search"
        :query="query"
        :custom-items-per-page="10"
        :query-variables="queryVariables"
        :get-csv-data-shape="getCsvDataShape"
        csvFilename="usuarios_bloqueados"
        query-name="usersBlockedDiscounts"
        export-all-result
        :export-all-result-incremental-page-size="100"
        @update-options="updateVariables"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td :class="{ 'texto-corrido': !hasPiiAccess }">
              <router-link
                v-if="hasPiiAccess"
                class="link link-color text-corrido"
                :to="{ path: `/clientes/detalhes-cliente/${item.document_no}` }"
                target="_blank"
              >
                {{ item.document_no | cpfCnpj }}
              </router-link>
              <v-tooltip v-else max-width="250" top content-class="truncated-tooltip">
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    {{ item.document_no }}
                  </span>
                </template>
                <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
              </v-tooltip>
            </td>
            <td class="texto-corrido">
              <v-tooltip max-width="250" top content-class="truncated-tooltip" :disabled="hasPiiAccess">
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    {{ item.name }}
                  </span>
                </template>
                <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
              </v-tooltip>
            </td>
            <td class="texto-corrido">{{ DateTime.fromISO(item.created_at).toFormat('dd/MM/yyyy') }}</td>
            <td class="texto-corrido text-end">
              <span v-if="isBlackListBlocked.active" class="link" @click="confirmUnlockClient(item._id, item.document_no)">Desbloquear</span>
              <span v-else class="disabled-text">Desbloquear</span>
            </td>
          </tr>
        </template>
      </paginated-data-table>
    </mf-card-container>
  </div>
</template>

<script>
import { QUERY_GET_CLIENTS_CANT_ACCUMULATE_CASHBACK, MUTATION_UNLOCK_USER_DISCOUNTS } from '../../graphql'
import { DateTime } from 'luxon'
import PaginatedDataTable from '@/components/PaginatedDataTable'
import cpfCnpj from '@/filters/cpfCnpj.js'
import ExportData from '@/components/export-data/ExportData.vue'

export default {
  name: 'CashbackAccumulatePoints',
  components: {
    PaginatedDataTable,
    ExportData,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    isDocumentsBlocked: false,
    switchValue: false,
    search: '',
    DateTime: DateTime,
    cpfCnpj: cpfCnpj,
    queryVariables: {
      sort: ['created_at'],
      sortDirection: ['DESC'],
    },
  }),
  computed: {
    isBlackListBlocked: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    query() {
      return QUERY_GET_CLIENTS_CANT_ACCUMULATE_CASHBACK
    },
    title() {
      return `Pessoas que não podem acumular cashback`
    },
    headers() {
      return [
        {
          text: 'CPF',
          value: 'document_no',
          width: '200px',
          sortable: true,
          isDefault: true,
        },
        {
          text: 'Consumidor',
          width: '300px',
          value: 'name',
          sortable: true,
          isDefault: true,
        },
        {
          text: 'Data de bloqueio',
          value: 'created_at',
          sortable: true,
          isDefault: true,
        },
        {
          text: '',
          value: '_id',
          sortable: true,
          align: 'end',
          isDefault: false,
        },
      ]
    },
    hasPiiAccess() {
      return this.$hasPiiAccess()
    },
  },
  mounted() {
    this.switchValue = this.isDocumentsBlocked.active
  },
  methods: {
    updateVariables(newQueryOptions) {
      const { sortBy, sortDesc } = newQueryOptions

      if (sortBy.length && sortDesc.length) {
        this.queryVariables.sort = sortBy
        this.queryVariables.sortDirection = sortDesc[0] ? ['DESC'] : ['ASC']
      }
    },
    getCsvDataShape(item) {
      return {
        document_no: this.cpfCnpj(item.document_no),
        name: item.name,
        created_at: DateTime.fromISO(item.created_at).toFormat('dd/MM/yyyy'),
      }
    },
    async getDataToExport() {
      return this.$refs.blockedUsers.exportCsv()
    },
    confirmUnlockClient(id, documentNo) {
      this.$confirm({
        title: 'Desbloquear usuário',
        message: `Ao desbloquear o CPF ${this.cpfCnpj(
          documentNo
        ).bold()} ele passará a poder acumular cashback nas compras realizadas abaixo do limite definido a partir do próximo dia. As compras realizadas hoje não irão acumular cashback.`,
        confirmText: 'Desbloquear',
        confirmColor: 'vermelho-d20',
        cancelText: 'Cancelar',
      })
        .then(() => {
          this.unlockClient(id)
        })
        .catch(() => {})
    },
    unlockClient(id) {
      this.$apollo
        .mutate({
          mutation: MUTATION_UNLOCK_USER_DISCOUNTS,
          variables: {
            _id: id,
          },
        })
        .then(() => {
          this.$snackbar({ message: 'Usuário desbloqueado com sucesso.', snackbarColor: 'success' })
          this.$refs.blockedUsers.waitAndGetData()
        })
        .catch(() => {
          this.$snackbar({ message: 'Erro ao desbloquear usuário.', snackbarColor: 'error' })
        })
    },
    goToBlockPage() {
      const returnName = 'Cashback'
      const returnTab = 1

      this.$router.push({ name: 'bloquear-cpf', params: { returnName: returnName, tab: returnTab } })
    },
    updateBlackListBlock() {
      this.$emit('update-blocks', 'Bloqueio de cpf atualizado com sucesso.')
    },
  },
}
</script>

<style lang="scss">
.disabled-text {
  color: rgba(0, 0, 0, 0.26) !important;
  text-decoration: none;
}
.btn-font-size {
  font-size: 18px !important;
}
</style>
